/**
 * desktopSignIn — the custom-lane sign-in surface's service layer
 * (2026-08-18 UX overhaul, phase 6; 2026-08-19 interactive-surface fix).
 *
 * A system outside the Composio catalog connects by the user signing into it
 * ONCE inside prest0n's own VM browser (the persistent per-user profile
 * keeps the session). This module owns:
 *   - starting the desktop session on the app's sign-in page (the exact
 *     POST /api/desktop contract useAgentActivity uses — no
 *     client-minted id, so the server's per-user reuse guard works). The
 *     response's wsPort is KEPT (2026-08-19): it is the interactive noVNC
 *     channel — the same recipe the VM's own /desktop page uses — and
 *     discarding it was why the sign-in surface had no way to type;
 *   - the client mirror of the server's custom-connection slug derivation
 *     (pinned by a parity test against functions/src/composio/index.ts);
 *   - session liveness (GET info) + keepalive (the server reaps idle
 *     desktops at 20 minutes; a slow 2FA sign-in must not be reaped);
 *   - the promotion call: 'registered' → 'connected' after the user attests
 *     the sign-in ("done — I'm signed in").
 */

import { firmConfirmsSignInApp } from '../../config/legalFirmConfirms.generated';
import { researchConfirmedHere } from '../legalApps/legalAgreementsClient';
import { getActiveWorkspaceAccount } from '../activeWorkspace';
import { getAuth } from 'firebase/auth';
import {
  getBrowserServiceBaseUrl,
  fetchWithBrowserServiceAuth,
  registerBrowserServiceToken,
} from '../browserService/baseUrl';
import { resolveAccountVmBrowserBase } from '../browserService/accountVmBase';
import { getConnectWarmupState, WARMUP_GATE_TTL_MS, WARMUP_VM_TTL_MS } from './connectWarmupState';
import { registerBrowserSessionBase } from '../browserService/sessionBaseRegistry';
import { knownSignInUrlForApp, registerResearchedSignInUrl, isSearchEngineSignInUrl } from '../globalTools/tools/connectApp';
import { fetchConnectResearch } from './ComposioClient';
import { logConnectFunnel } from './connectPopupFlow';
import {
  ensureWorkspaceReadyForConnect,
  type WorkspaceBootProgress,
  type WorkspaceReadyOutcome,
} from '../browserService/workspaceBootGate';
// The sign-in host rules (contract C5, 2026-09-02): imported, never re-typed
// here — tests/onboarding/signinHostRulesParity.test.ts pins that this file
// carries no inline copy of the auth-host / ccTLD-suffix regexes.
import { isAuthHost, registrableDomain } from './signinHosts';

/**
 * Client mirror of the server slug derivation in functions/src/composio/
 * index.ts (case 'custom-connections' POST): name → `custom-<slugBody>`.
 * Keep byte-for-byte in sync — a drift strands promotions on a 404.
 */
export function customSlugForName(name: string): string {
  const slugBody = String(name || '')
    .trim()
    .slice(0, 80)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);
  return slugBody ? `custom-${slugBody}` : '';
}

export interface DesktopSignInSession {
  sessionId: string;
  base: string;
  /** websockify port for the interactive noVNC view; null when the service
   *  build predates the field (the surface then leans on the deep link). */
  wsPort: number | null;
  /** The destination the journey resolved for this app (2026-08-20 Kalshi
   *  repro) — the modal's pilot, page-state scope, retarget belt, and
   *  guidance all key on it, whether or not the caller had a URL. Empty on
   *  a handoff adoption with nothing known (belt/guidance then stand down). */
  resolvedSignInUrl: string;
  /** Where the destination came from. 'none' means nothing is known: the
   *  desktop opens without navigating and Prest0n's pilot goes looking —
   *  honest, and never a fabricated domain. Only a verified-grade source
   *  (explicit/known/researched) is persisted as the verifier's probe
   *  target (review finding 2026-08-21: an unverified domain showing a
   *  login page would otherwise downgrade a working connection forever). */
  signInUrlSource: 'explicit' | 'known' | 'researched' | 'none';
  /** This desktop's live-view pass (2026-09-24, S1a): rides the viewer
   *  socket's path, so a workstation that admits a live view only with its
   *  desktop's token admits this one. null from a service build without it. */
  viewerToken?: string | null;
}

/** A live-view pass as the workstation mints it (base64url, 32+ characters),
 *  or null. Anything else is dropped rather than put into a URL. */
export function viewerTokenOf(value: unknown): string | null {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{32,128}$/.test(value) ? value : null;
}

/**
 * Resolve WHERE this sign-in journey is going — BEFORE any desktop opens
 * (2026-08-20 Kalshi repro: a session started with no real destination
 * parked the user on a default page with zero help). The ladder:
 *   1. an explicit researched https URL (carried on the connect card);
 *   2. the static map / researched registry (knownSignInUrlForApp);
 *   3. the SERVER's connect-research brain (fetchConnectResearch — the same
 *      SSOT route the primary orchestrator uses; cached 14d server-side) —
 *      this is prest0n primary supplying the destination even on surfaces
 *      with no chat mounted;
 *   4. NOTHING — { url: '', source: 'none' }.
 *
 * Rung 4 used to fabricate `https://www.<app>.com` (2026-09-02, contract C8
 * of docs/PLAN-non-direct-app-connections-20260901.md): a made-up domain is
 * a destination the platform cannot stand behind — it parked users on
 * parked-domain pages, it could not be stored as a probe target, and it made
 * the guidance copy hedge on every rung. With nothing known the desktop
 * opens WITHOUT navigating (postDesktopSession omits the url) and the pilot
 * goes looking from there. Never throws; MAY return an empty url.
 */
export interface SignInDestination {
  url: string;
  source: 'explicit' | 'known' | 'researched' | 'none';
}

/**
 * The ONE rule for "is this handed-in URL a destination we may drive to":
 * https, and not a search engine. Every rung of both ladders runs a URL
 * through it, and so must any surface that keeps its own fallback to the
 * caller's prop — the deterministic navigation paths trust this, whatever a
 * card or a model handed us. Returns the usable URL, or '' (never throws).
 */
export function usableSignInDestination(signInUrl?: string): string {
  const candidate = (signInUrl || '').trim();
  if (!/^https:\/\//i.test(candidate)) return '';
  return isSearchEngineSignInUrl(candidate) ? '' : candidate;
}

export async function resolveSignInDestination(
  appName: string,
  signInUrl?: string
): Promise<SignInDestination> {
  const explicit = usableSignInDestination(signInUrl);
  if (explicit) {
    return { url: explicit, source: 'explicit' };
  }
  const known = knownSignInUrlForApp(appName);
  if (known && !isSearchEngineSignInUrl(known)) return { url: known, source: 'known' };
  try {
    // Bounded: research is served from cache in the common case; a cold
    // research run is worth a short wait — it is the difference between
    // the right login page and a guess.
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 8_000));
    const researched = await Promise.race([
      fetchConnectResearch(appName).then((r) => r?.research?.signInUrl || null).catch(() => null),
      timeout,
    ]);
    if (researched && /^https:\/\//i.test(researched) && !isSearchEngineSignInUrl(researched)) {
      registerResearchedSignInUrl(appName, researched);
      logConnectFunnel({ event: 'destination_researched', detail: researched });
      return { url: researched, source: 'researched' };
    }
  } catch { /* nothing researched — fall through to 'none' */ }
  return { url: '', source: 'none' };
}

/**
 * Synchronous destination ladder for HANDOFF adoption (review finding
 * 2026-08-21: research must never delay adopting an already-live session).
 * Explicit URL, then the static map / researched registry — no network. A
 * chat handoff came from the card lane, whose research already populated
 * the registry, so 'none' here genuinely means nothing is known and the
 * belt/guidance stand down.
 */
export function resolveKnownSignInDestinationSync(
  appName: string,
  signInUrl?: string
): { url: string; source: 'explicit' | 'known' | 'none' } {
  const explicit = usableSignInDestination(signInUrl);
  if (explicit) {
    return { url: explicit, source: 'explicit' };
  }
  const known = knownSignInUrlForApp(appName);
  if (known && !isSearchEngineSignInUrl(known)) return { url: known, source: 'known' };
  return { url: '', source: 'none' };
}

/** Anonymous desktop identity (2026-08-20): NEVER a shared literal — the old
 *  'chat-user' fallback pooled every signed-out visitor into ONE master
 *  browser profile on the VM, sharing their sessions with each other. A
 *  per-tab random id keeps anonymous desktops isolated (and unpersisted
 *  across visits — correct: there is no identity to persist for). */
let anonDesktopId: string | null = null;
function anonDesktopUserId(): string {
  if (!anonDesktopId) {
    anonDesktopId = `anon-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  }
  return anonDesktopId;
}

async function postDesktopSession(
  base: string,
  destinationUrl?: string,
  opts: { app?: string; resume?: boolean } = {}
): Promise<{ sessionId: string; wsPort: number | null; viewerToken: string | null }> {
  const userId = getAuth().currentUser?.uid || anonDesktopUserId();
  const app = typeof opts.app === 'string' ? opts.app.trim().slice(0, 80) : '';
  const res = await fetchWithBrowserServiceAuth(base, `${base}/api/desktop`, {
    method: 'POST',
    // The person's own sign-in window (2026-09-29, PR-5): an address it sends is opened unless a
    // computer task is driving the browser and not yet waiting for this window (page-change.ts).
    headers: { 'Content-Type': 'application/json', 'X-Prest0n-Surface': 'sign-in-window' },
    // No url = create/reuse the session WITHOUT navigating (the intent
    // warmup's pre-create); the server only launches Chromium when a url
    // rides the request. `app` (2026-09-29) names whose tab the address opens
    // in, so a later run or sign-in for that app finds the same tab; an older
    // workstation ignores it.
    // `resume` (slice A3, 2026-10-02): the person's own sign-in window, opened again. On a desktop already
    // running, the workstation carries on in the app's tab instead of loading the sign-in page over it. Sent
    // only by startSignInDesktopSession, never by the connect card's pre-create; an older workstation ignores it.
    body: JSON.stringify({
      ...(destinationUrl ? { url: destinationUrl } : {}),
      ...(app ? { app } : {}),
      ...(opts.resume === true ? { resume: true } : {}),
      userId,
    }),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(String((errBody as any)?.error || `HTTP ${res.status}`));
  }
  const data = (await res.json()) as { sessionId: string; wsPort?: number; viewerToken?: unknown };
  if (!data?.sessionId) throw new Error('desktop session did not return a session id');
  return {
    sessionId: data.sessionId,
    wsPort: Number.isFinite(data.wsPort) ? Number(data.wsPort) : null,
    viewerToken: viewerTokenOf(data.viewerToken),
  };
}

/**
 * Pre-create (or re-touch) the caller's desktop session at connect-INTENT
 * time (2026-08-31 instant-connect) — the same idempotent POST the click
 * path makes, fired early so the click-time POST is a near-instant reuse
 * hit with the X stack already up (and Chromium already navigating when the
 * destination was known). The server's per-user reuse guard is what makes
 * this safe to fire speculatively. Never throws; null on any failure.
 */
export async function preCreateDesktopSession(
  base: string,
  destinationUrl?: string,
  opts: { app?: string } = {}
): Promise<{ sessionId: string; wsPort: number | null; viewerToken: string | null } | null> {
  try {
    // 2026-09-29 (docs/PLAN-one-browser-many-windows-20260929.md): this fires when a connect card
    // RENDERS, from any thread, before anyone clicked. An older workstation loads an address sent
    // on create into whatever tab is in front, which is how a Slides task moved Miles's eFax tab
    // under him. So the address rides only to a workstation that opens it in the app's own tab
    // and never moves a browser someone is using (its /health says `tasks.ownTab: "on"`); any
    // other one is warmed without it, and the sign-in window's own click still loads the page.
    const url = destinationUrl && (await opensAddressesInTheirOwnTab(base)) ? destinationUrl : undefined;
    const created = await postDesktopSession(base, url, opts);
    registerBrowserSessionBase(created.sessionId, base);
    return created;
  } catch {
    return null;
  }
}

/** How long a workstation's answer to "does an address open in its own tab?" is trusted. */
const OWN_TAB_TTL_MS = 5 * 60_000;
const OWN_TAB_TIMEOUT_MS = 3_500;
const ownTabByBase = new Map<string, { yes: boolean; atMs: number }>();

/**
 * Whether the workstation at `base` opens an address sent to a running browser in the app's own
 * tab, never over a tab someone is using (browser-service /health `tasks.ownTab`, 2026-09-29).
 * False on any doubt: no answer, an older build, the switch off. A no is remembered as briefly as
 * a yes, so a machine that updates is believed within minutes.
 */
export async function opensAddressesInTheirOwnTab(base: string): Promise<boolean> {
  const key = String(base || '').replace(/\/+$/, '');
  if (!key) return false;
  const known = ownTabByBase.get(key);
  if (known && Date.now() - known.atMs < OWN_TAB_TTL_MS) return known.yes;
  let yes = false;
  try {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), OWN_TAB_TIMEOUT_MS) : null;
    try {
      const res = await fetch(`${key}/health`, { method: 'GET', ...(controller ? { signal: controller.signal } : {}) });
      const body = res.ok ? ((await res.json().catch(() => null)) as { tasks?: { ownTab?: unknown } } | null) : null;
      yes = body?.tasks?.ownTab === 'on';
    } finally {
      if (timer) clearTimeout(timer);
    }
  } catch {
    yes = false;
  }
  ownTabByBase.set(key, { yes, atMs: Date.now() });
  return yes;
}

/** @internal — tests only. */
export function __resetOwnTabAnswersForTests(): void {
  ownTabByBase.clear();
}

// ─── A person's window onto Prest0n's browser (2026-09-29) ───────────────────
//
// docs/PLAN-one-browser-many-windows-20260929.md, PR-3. A chat window that shows the browser (the
// run's live view, the sign-in window) HOLDS it while it is open: the keepalive route takes
// `presence` (browser-service server.ts), nothing new starts and no address is loaded while the
// hold stands, and a running task waits at its next step. usePersonHold owns the lease; this is
// its one call.

/** What waits for the browser while a person holds it (the workstation's words, validated). */
export interface BrowserWait {
  app?: string;
  state: 'waiting' | 'paused';
  /** PR-10: `app-window`, `no-room` and `watched` wait for another task, never for this window. */
  reason: 'person-using-browser' | 'person-signing-in' | 'another-task' | 'app-window' | 'no-room' | 'watched';
}

/** The keepalive's answer to a presence: a hold (or none, when the desktop has as many windows
 *  as it holds), or `supported: false` from a workstation that does not take presence. */
export type DesktopPresenceAnswer =
  | { supported: true; holdId: string | null; renewEveryMs: number; waiting: BrowserWait[] }
  | { supported: false };

export type DesktopPresence =
  | {
      op: 'hold';
      holdId?: string;
      surface: string;
      allowRunIds?: readonly string[];
      allowRunning?: boolean;
      show?: { app?: string; url?: string };
    }
  | { op: 'release'; holdId: string };

const BROWSER_WAIT_REASONS: ReadonlySet<string> = new Set(['person-using-browser', 'person-signing-in', 'another-task', 'app-window', 'no-room', 'watched']);

/**
 * Tell the workstation a chat window onto the browser is open (a hold, renewed), or closed.
 * 'gone' when the desktop no longer exists (404); null on any other failure. `keepalive` lets a
 * release sent as the page goes away outlive the page.
 */
export async function sendDesktopPresence(
  sessionId: string,
  base: string,
  presence: DesktopPresence,
  opts: { keepalive?: boolean } = {}
): Promise<DesktopPresenceAnswer | 'gone' | null> {
  try {
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/keepalive`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presence }),
        ...(opts.keepalive ? { keepalive: true } : {}),
      }
    );
    if (res.status === 404) return 'gone';
    if (!res.ok) return null;
    const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!body || typeof body !== 'object') return null;
    // A workstation that takes presence answers a hold with its lease (`holdId`, a string or
    // null) and how often to renew it; an older one answers the plain ping `{ok:true}`.
    if (!('holdId' in body) && !('renewEveryMs' in body) && !('released' in body)) return { supported: false };
    const holdId = typeof body.holdId === 'string' && body.holdId.length <= 64 ? body.holdId : null;
    const renew = Number(body.renewEveryMs);
    const waiting: BrowserWait[] = Array.isArray(body.waiting)
      ? (body.waiting as unknown[]).flatMap((raw): BrowserWait[] => {
          if (!raw || typeof raw !== 'object') return [];
          const w = raw as Record<string, unknown>;
          const state = w.state === 'paused' ? 'paused' : w.state === 'waiting' ? 'waiting' : null;
          if (!state) return [];
          const reason = typeof w.reason === 'string' && BROWSER_WAIT_REASONS.has(w.reason) ? (w.reason as BrowserWait['reason']) : 'another-task';
          const app = typeof w.app === 'string' && w.app.trim() ? w.app.trim().slice(0, 80) : '';
          return [{ ...(app ? { app } : {}), state, reason }];
        }).slice(0, 12)
      : [];
    return { supported: true, holdId, renewEveryMs: Number.isFinite(renew) && renew > 0 ? renew : 15_000, waiting };
  } catch {
    return null;
  }
}

/**
 * The workstation could not host the sign-in and the shared service would
 * be the WRONG machine (its profile is one prest0n never reads for this
 * account). Carries the gate outcome so the surface can say exactly what is
 * happening instead of a generic failure.
 */
export class WorkspaceNotReadyError extends Error {
  readonly outcome: Exclude<WorkspaceReadyOutcome['kind'], 'ready' | 'shared'>;
  readonly reason?: string;
  constructor(
    outcome: Exclude<WorkspaceReadyOutcome['kind'], 'ready' | 'shared'>,
    reason?: string
  ) {
    super(`workspace not ready: ${outcome}${reason ? ` (${reason})` : ''}`);
    this.name = 'WorkspaceNotReadyError';
    this.outcome = outcome;
    this.reason = reason;
    // ES5 target: Error subclasses lose their prototype chain without this,
    // and the modal's instanceof dispatch silently falls to the retry path.
    Object.setPrototypeOf(this, WorkspaceNotReadyError.prototype);
  }
}

/**
 * A sign-in to a research service whose terms forbid automation (Lexis+, Westlaw, Bloomberg Law, vLex):
 * nothing of Prest0n's signs in to one until an owner of the workspace confirms that the firm has the
 * provider's written permission (legal apps 1d and 8, docs/PLAN-legal-apps-20260928.md). The sign-in window
 * shows its own fixed words for it; the server refuses the card, the registration and the checks.
 */
export class ResearchAgreementRequiredError extends Error {
  readonly appName: string;
  readonly vendor: string;
  /** The catalog key of the service (lexisnexis, westlaw, …). */
  readonly appKey: string;
  constructor(appName: string, vendor: string, appKey = '') {
    super(`${appName} waits for an owner to confirm the firm's written permission`);
    this.name = 'ResearchAgreementRequiredError';
    this.appName = appName;
    this.vendor = vendor;
    this.appKey = appKey;
    // ES5 target: without this, `instanceof` fails on a thrown instance and the sign-in window would read
    // the refusal as an unreachable workstation (the WorkspaceNotReadyError fix; pinned by the tests).
    Object.setPrototypeOf(this, ResearchAgreementRequiredError.prototype);
  }
}

/**
 * The research service a sign-in names by its name or its address, as the refusal it would get, or null (the small
 * generated list). Asked synchronously, before anything starts; researchAgreementRefusalHere asks the server whether
 * an owner lifted it.
 */
export function researchAgreementRefusal(appName: string, url?: string | null): ResearchAgreementRequiredError | null {
  const app = firmConfirmsSignInApp(appName, url);
  return app ? new ResearchAgreementRequiredError(app.name, app.vendor, app.key) : null;
}

/**
 * Legal apps 8: the refusal, unless an owner of the workspace (`accountId`, else the active one) has confirmed the
 * firm's permission for that service. Only a readable "confirmed" lifts it: a read that fails refuses, as before an
 * owner could confirm. Other apps never ask the server.
 */
export async function researchAgreementRefusalHere(
  appName: string,
  url?: string | null,
  accountId?: string | null,
): Promise<ResearchAgreementRequiredError | null> {
  const refusal = researchAgreementRefusal(appName, url);
  if (!refusal) return null;
  const confirmed = await researchConfirmedHere(refusal.appKey, accountId || getActiveWorkspaceAccount()).catch(() => false);
  return confirmed ? null : refusal;
}

/**
 * Start (or reuse) a desktop session on the app's sign-in page, with the
 * same create call the /desktop page makes. Throws with the server's message
 * on failure — callers own the retry/backoff presentation.
 *
 * Wake-aware (2026-08-19): the workspaceBootGate runs FIRST — a sleeping
 * workstation is woken (honest progress via opts.onBootProgress), a missing
 * one is provisioned on the spot (owner decision), and gate dead-ends throw
 * WorkspaceNotReadyError. Accounts WITH a workstation are never silently
 * routed to the platform-shared service any more: the old ladder's fallback
 * put the login in a browser profile the org's prest0n never reads — a
 * connection that looked done and then didn't work. The shared service now
 * hosts sign-ins only when it genuinely is the account's machine (internal
 * accounts) or the account has no workstation lane at all.
 */
export async function startSignInDesktopSession(
  appName: string,
  opts?: {
    signInUrl?: string;
    onBootProgress?: (progress: WorkspaceBootProgress) => void;
    /** Explicit workstation target (2026-08-23 warm-from-arrival): the
     *  reserved/minted org id during onboarding — the back-pointer still
     *  names the user's PREVIOUS workspace until the completion switch, and
     *  a sign-in must land on the ORG's machine, never the old one. */
    workspaceAccountId?: string;
  }
): Promise<DesktopSignInSession> {
  const t0 = Date.now();
  // Legal apps 1d: a research service whose terms forbid automation is never signed in to through
  // Prest0n's browser until an owner can confirm the firm's written permission — asked by name before
  // anything wakes, and again by the resolved address below, before any desktop is made.
  const refusedByName = await researchAgreementRefusalHere(appName, opts?.signInUrl, opts?.workspaceAccountId);
  if (refusedByName) {
    logConnectFunnel({ event: 'session_start_failed', detail: 'legal-agreement' });
    throw refusedByName;
  }
  // Destination resolution starts FIRST and runs concurrently with the wake
  // gate (review finding 2026-08-21: a serial research call sat in the boot
  // critical path — a minutes-long VM wake should absorb it for free). The
  // journey still never opens a desktop without knowing where it is going:
  // the await sits before the POST below.
  const destinationPromise = resolveSignInDestination(appName, opts?.signInUrl);

  // Connect-intent warmup (2026-08-31 instant-connect): when a warmup ran
  // recently for this target (card render / panel mount / onboarding step),
  // its gate answer and health-verified VM base are consumed here instead of
  // re-paying the CF round trip and the first TLS contact on the click path.
  // Any miss falls through to the live chain unchanged.
  const warm = getConnectWarmupState(opts?.workspaceAccountId);
  const warmGateFresh = Boolean(
    warm && warm.gateReadyAt !== null && Date.now() - warm.gateReadyAt < WARMUP_GATE_TTL_MS
  );
  // Whether THIS boot woke/provisioned the workstation — decides the vm/main
  // read's cache posture below.
  let booted = false;
  let gate: WorkspaceReadyOutcome;
  if (warmGateFresh && warm) {
    gate = { kind: 'ready', ...(warm.gateAccountId ? { accountId: warm.gateAccountId } : {}) };
  } else {
    gate = await ensureWorkspaceReadyForConnect({
      onProgress: (p) => {
        booted = true;
        opts?.onBootProgress?.(p);
      },
      ...(opts?.workspaceAccountId ? { accountId: opts.workspaceAccountId } : {}),
    });
  }
  if (gate.kind !== 'ready' && gate.kind !== 'shared') {
    logConnectFunnel({ event: 'session_start_failed', detail: `gate:${gate.kind}` });
    throw new WorkspaceNotReadyError(gate.kind, 'reason' in gate ? gate.reason : undefined);
  }
  logConnectFunnel({
    event: 'boot_gate_passed',
    ms: Date.now() - t0,
    detail: warmGateFresh ? 'warmup-cache' : booted ? 'booted' : 'live',
  });

  const destination = await destinationPromise;
  const refusedByAddress = await researchAgreementRefusalHere('', destination.url, opts?.workspaceAccountId);
  if (refusedByAddress) {
    logConnectFunnel({ event: 'session_start_failed', detail: 'legal-agreement' });
    throw refusedByAddress;
  }

  const sharedBase = await getBrowserServiceBaseUrl();
  if (gate.kind === 'ready') {
    // Which workspace to resolve the VM against: the caller's explicit
    // target first, else the account the GATE answered for (2026-08-27
    // pending-org fallback: mid-onboarding the server substitutes the
    // caller's own org for the stale back-pointer plane — following its
    // echo here is what stops the sign-in from re-resolving the wrong
    // plane and dead-ending on "could not reach the workstation browser").
    const vmAccountId = opts?.workspaceAccountId
      ?? (typeof gate.accountId === 'string' && gate.accountId.startsWith('co_') ? gate.accountId : undefined);
    // The warmup's health-verified base short-circuits the Firestore read
    // when it matches this boot's target; otherwise resolve live — fresh
    // ONLY after a wake/provision (2026-08-31: the unconditional fresh:true
    // re-read was a 100-400ms tax on every fully-warm boot; the page cache
    // is only stale when this boot itself changed the machine).
    const warmVm = warm && warm.vm && Date.now() - warm.vm.verifiedAt < WARMUP_VM_TTL_MS
      && (!vmAccountId || warm.vm.accountId === vmAccountId)
      ? warm.vm
      : null;
    const vm = warmVm ?? await resolveAccountVmBrowserBase({
      ...(booted ? { fresh: true } : {}),
      ...(vmAccountId ? { accountId: vmAccountId } : {}),
    }).catch(() => null);
    if (vm) {
      // Per-VM browser-service token (P0 hardening): register it so every
      // fetch to this VM's …/browser-api host uses the VM's OWN token, not
      // the platform-shared one.
      registerBrowserServiceToken(vm.base, vm.token, vm.accountId);
    }
    if (vm && vm.base !== sharedBase) {
      // No pre-POST health probe (2026-08-31 instant-connect): the POST is
      // the probe — its own doc called the old serial GET "the caller's
      // actual POST remains the real proof" — and the modal's retry ladder
      // owns transient failures. That probe cost a full first-TLS round
      // trip (0.1-3.5s) on every warm click; the intent warmup now pays it
      // off the click path instead.
      try {
        const { sessionId, wsPort, viewerToken } = await postDesktopSession(vm.base, destination.url, { app: appName, resume: true });
        registerBrowserSessionBase(sessionId, vm.base);
        logConnectFunnel({ event: 'session_on_account_vm', detail: vm.accountId });
        logConnectFunnel({ event: 'session_created', ms: Date.now() - t0 });
        return { sessionId, base: vm.base, wsPort, ...(viewerToken ? { viewerToken } : {}), resolvedSignInUrl: destination.url, signInUrlSource: destination.source };
      } catch (err) {
        // Funnel parity with the shared-base branch below (review finding on
        // PR #1350): with the probe gone this POST is the FIRST failure
        // signal on the workstation path — post-wake "VM not answering yet"
        // is the most common miss ES-17's evidence needs to count. Logged,
        // then rethrown: the modal's retry ladder still owns presentation.
        logConnectFunnel({
          event: 'session_start_failed',
          detail: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    }
    // A ready workstation whose base can't be resolved safely — refuse
    // rather than sign into the wrong machine.
    if (vm === null) {
      logConnectFunnel({ event: 'session_start_failed', detail: 'vm base unresolved' });
      throw new Error('could not reach the workstation browser — try again in a moment');
    }
  }

  try {
    const { sessionId, wsPort, viewerToken } = await postDesktopSession(sharedBase, destination.url, { app: appName, resume: true });
    registerBrowserSessionBase(sessionId, null);
    logConnectFunnel({ event: 'session_created', ms: Date.now() - t0, detail: 'shared' });
    return { sessionId, base: sharedBase, wsPort, ...(viewerToken ? { viewerToken } : {}), resolvedSignInUrl: destination.url, signInUrlSource: destination.source };
  } catch (err) {
    logConnectFunnel({
      event: 'session_start_failed',
      detail: err instanceof Error ? err.message : String(err),
    });
    throw err;
  }
}

/**
 * The interactive noVNC URL for a desktop session — the SAME recipe the VM's
 * own /desktop realtime page uses (bootstrap/public/preston-realtime.html):
 * Caddy proxies /novnc/<wsPort>/* to the session's websockify, and vnc.html
 * with view_only=0 is the full keyboard+mouse surface. No bundler involved —
 * the page is served by the VM itself, so an <iframe> is all the client
 * needs. Returns null when the session carried no wsPort (old service build)
 * or the base is not an https origin — callers fall back to the deep link.
 */
export function buildDesktopNoVncUrl(
  base: string,
  wsPort: number | null,
  /**
   * Makes the viewer page's address unique to one desktop session
   * (2026-09-23): websockify sends no cache headers, so a browser may keep a
   * viewer page from before the workstation updated — one that names a
   * keyboard script (viewer-keys) the update replaced.
   */
  cacheKey?: string,
  /**
   * The desktop's live-view pass (2026-09-24, S1a). With one, the socket path
   * moves to the address's #fragment (`#path=…?token=…`), which noVNC 1.0 and
   * 1.3 both read ahead of the query and which never reaches a server log;
   * without one the address is exactly what it was.
   */
  viewerToken?: string | null,
): string | null {
  if (!wsPort || !Number.isFinite(wsPort)) return null;
  let origin: string;
  try {
    const u = new URL(base);
    if (u.protocol !== 'https:') return null;
    origin = u.origin;
  } catch {
    return null;
  }
  const p = Math.floor(wsPort);
  // reconnect=1 (2026-08-31 instant-connect): the server side already
  // respawns a dead websockify/x11vnc (INCIDENT-20260820 supervision), but
  // with reconnect=0 the viewer never re-attached — connected panes stayed
  // dead until the modal was reopened. Auto-reconnect makes that existing
  // recovery user-visible.
  const socketPath = `novnc/${p}/websockify`;
  const token = viewerTokenOf(viewerToken);
  // quality=9&compression=2 (2026-10-02, slice B): the clearest picture noVNC's Tight encoding sends,
  // lightly compressed, so a captcha's pictures stay sharp. An older noVNC ignores both.
  return (
    `${origin}/novnc/${p}/vnc.html` +
    `?autoconnect=1&reconnect=1&reconnect_delay=2000&resize=scale&view_only=0&shared=1&quality=9&compression=2` +
    (token ? '' : `&path=${socketPath}`) +
    (cacheKey ? `&v=${encodeURIComponent(cacheKey)}` : '') +
    (token ? `#path=${encodeURIComponent(`${socketPath}?token=${token}`)}` : '')
  );
}

/**
 * Session liveness probe: GET /api/desktop/:id on the session's own base.
 * Null on ANY failure (gone, unreachable, old build) — never throws.
 */
export async function getDesktopSessionInfo(
  sessionId: string,
  base: string
): Promise<{ status: string; wsPort: number | null; viewerToken?: string } | null> {
  try {
    const res = await fetchWithBrowserServiceAuth(base, `${base}/api/desktop/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as
      | { status?: string; wsPort?: number; viewerToken?: unknown }
      | null;
    if (!data) return null;
    const viewerToken = viewerTokenOf(data.viewerToken);
    return {
      status: String(data.status || 'unknown'),
      wsPort: Number.isFinite(data.wsPort) ? Number(data.wsPort) : null,
      // Only when the workstation minted one: an older build's answer keeps its shape.
      ...(viewerToken ? { viewerToken } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Keepalive ping (2026-08-19): the server reaps idle desktops at 20 minutes;
 * the sign-in surface pings while open so a slow sign-in (2FA, password
 * reset) is never reaped mid-flow. Best-effort; false on any failure.
 */
export async function sendDesktopKeepalive(sessionId: string, base: string): Promise<boolean> {
  try {
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/keepalive`,
      { method: 'POST' }
    );
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Detection-assisted completion (2026-08-19): does the session's visible
 * page currently look signed in? Lights the one-tap "looks like you're in —
 * finish connecting" confirm; the human tap remains the promotion. Null on
 * ANY failure (route missing on old VMs, network) — the surface degrades to
 * plain attestation. Never throws.
 */
/** What KIND of page the live desktop is showing — the client mirror of the
 *  VM's page-state vocabulary. 'challenge' = a bot check only the human can
 *  pass; '2fa' = a code prompt; 'login' = credential entry; 'search' = a
 *  search-engine page (never a sign-in destination); 'notfound' = the page
 *  is gone (404/410 or a not-found body) — the destination itself is wrong,
 *  whoever's host it sits on; 'signup' = a create-an-account page, which is
 *  not a sign-in. The last two arrive with the workstation classifier
 *  (2026-09-02, contract C4): an older VM never sends them and an older
 *  client coerced them to 'other', so both directions degrade quietly. */
export type DesktopPageKind =
  | 'login' | '2fa' | 'challenge' | 'search' | 'signedin' | 'notfound' | 'signup' | 'other';

/** The one coercion list — every reader of a VM-sent `kind` narrows through
 *  it, so an unknown value can never reach the surface as a live kind.
 *
 *  ADDING A KIND CHANGES THE SURFACE'S WATCHES (2026-09-02 review finding):
 *  before 'notfound'/'signup' were listed here they coerced to 'other' and
 *  were judged by the modal's off-app watch like any other page. 'notfound'
 *  is now off-target on ANY host (plan row D2) and 'signup' stays judged by
 *  host — a create-account page on the app's own domain is a legitimate stop,
 *  one on somebody else's host is not. A kind that matches no arm of that
 *  watch is silently EXEMPT from being re-driven, so every new kind must be
 *  given a home there in the same change. */
export const DESKTOP_PAGE_KINDS: DesktopPageKind[] = [
  'login', '2fa', 'challenge', 'search', 'signedin', 'notfound', 'signup', 'other',
];

/** Viewport-relative rect (percent) of the human-only obstacle, for anchoring
 *  guidance overlays at the exact thing the user must interact with. */
export interface ObstacleRect {
  xPct: number;
  yPct: number;
  wPct: number;
  hPct: number;
}

export interface SignInStatus {
  signedIn: boolean;
  confidence: 'high' | 'medium' | 'low';
  url: string;
  kind: DesktopPageKind;
  obstacle: ObstacleRect | null;
  /** Slice F3: the workstation saw the person land on the app's own pages after its sign-in page
   *  (browser-service signin-landing.ts). Present only when the workstation says so. */
  landed?: boolean;
  /** Slice G2: the workstation is still saving the password the person just
   *  signed in with. Present (true) only then. */
  passwordSaving?: true;
  /** Slice G2: the save was accepted on a login that asked for the username
   *  first, so the window offers one field for it. Present (true) only then. */
  usernameWanted?: true;
}

/** PURE. The two save facts of a status answer, read strictly: only a literal
 *  true counts, and an answer without them (the lever off, an older
 *  workstation) adds nothing to the status. */
export function saveFactsOf(data: unknown): Pick<SignInStatus, 'passwordSaving' | 'usernameWanted'> {
  const d = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    ...(d.passwordSaving === true ? { passwordSaving: true as const } : {}),
    ...(d.usernameWanted === true ? { usernameWanted: true as const } : {}),
  };
}

/**
 * Detection-assisted status of the live desktop page, scoped to the app being
 * connected (2026-08-20). Passing appName/signInUrl makes a positive verdict
 * require the page's host to plausibly belong to that app — and it is never
 * positive on a search or bot-challenge page. Also reports the page KIND and
 * the obstacle rect so the surface can guide the human. Null on any failure
 * (old VM / transport) → the surface degrades to plain attestation.
 */
export interface SignInStatusProbe {
  status: SignInStatus | null;
  /** True ONLY on a definitive 404 — the old-build "route missing" signal.
   *  A transient failure (502, timeout, auth mint) leaves this false with a
   *  null status, so callers can tell "this VM can't answer" apart from
   *  "this VM didn't answer just now" (review finding 2026-08-21: the belt
   *  conflated the two and could clobber an in-progress sign-in). */
  routeMissing: boolean;
}

export async function checkSignInStatusDetailed(
  sessionId: string,
  base: string,
  scope?: { appName?: string; signInUrl?: string }
): Promise<SignInStatusProbe> {
  try {
    const params = new URLSearchParams();
    if (scope?.appName) params.set('app', scope.appName.slice(0, 80));
    if (scope?.signInUrl && /^https:\/\//i.test(scope.signInUrl)) params.set('expected', scope.signInUrl.slice(0, 500));
    const qs = params.toString();
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/signin-status${qs ? `?${qs}` : ''}`
    );
    if (res.status === 404) {
      // Two DIFFERENT 404s (2026-08-22 Fitbit incident): the old-build
      // catch-all (route absent) vs a CURRENT build answering 'session
      // not found' (VM restarted / session idle-reaped). Treating the
      // second as an old build made the belt navigate a dead session and
      // raise the blind guidance floor over whatever the page showed.
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      const sessionGone = typeof body?.error === 'string' && /session/i.test(body.error);
      return { status: null, routeMissing: !sessionGone };
    }
    if (!res.ok) return { status: null, routeMissing: false };
    const data = (await res.json().catch(() => null)) as
      | { ok?: boolean; signedIn?: boolean; confidence?: string; url?: string; kind?: string; obstacle?: ObstacleRect | null; landed?: unknown }
      | null;
    if (!data || data.ok !== true || typeof data.signedIn !== 'boolean') {
      return { status: null, routeMissing: false };
    }
    const confidence =
      data.confidence === 'high' || data.confidence === 'medium' ? data.confidence : 'low';
    const kind = DESKTOP_PAGE_KINDS.includes(data.kind as DesktopPageKind)
      ? (data.kind as DesktopPageKind)
      : 'other';
    return {
      status: {
        signedIn: data.signedIn,
        confidence,
        url: typeof data.url === 'string' ? data.url : '',
        kind,
        obstacle: data.obstacle && typeof data.obstacle === 'object' ? data.obstacle : null,
        ...(data.landed === true ? { landed: true } : {}),
      },
      routeMissing: false,
    };
  } catch {
    return { status: null, routeMissing: false };
  }
}

export async function checkSignInStatus(
  sessionId: string,
  base: string,
  scope?: { appName?: string; signInUrl?: string }
): Promise<SignInStatus | null> {
  try {
    const params = new URLSearchParams();
    if (scope?.appName) params.set('app', scope.appName.slice(0, 80));
    if (scope?.signInUrl && /^https:\/\//i.test(scope.signInUrl)) params.set('expected', scope.signInUrl.slice(0, 500));
    const qs = params.toString();
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/signin-status${qs ? `?${qs}` : ''}`
    );
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as
      | { ok?: boolean; signedIn?: boolean; confidence?: string; url?: string; kind?: string; obstacle?: ObstacleRect | null; landed?: unknown }
      | null;
    if (!data || data.ok !== true || typeof data.signedIn !== 'boolean') return null;
    const confidence =
      data.confidence === 'high' || data.confidence === 'medium' ? data.confidence : 'low';
    const kind = DESKTOP_PAGE_KINDS.includes(data.kind as DesktopPageKind)
      ? (data.kind as DesktopPageKind)
      : 'other';
    return {
      signedIn: data.signedIn,
      confidence,
      url: typeof data.url === 'string' ? data.url : '',
      kind,
      obstacle: data.obstacle && typeof data.obstacle === 'object' ? data.obstacle : null,
      ...(data.landed === true ? { landed: true } : {}),
      ...saveFactsOf(data),
    };
  } catch {
    return null;
  }
}

/** A guidance pop-over the primary/computer-use plane composed for a
 *  human-only step, ready to render over the live desktop view. */
export interface OverlayGuide {
  text: string;
  xPct: number;
  yPct: number;
  affordance: 'none' | 'arrow' | 'circle';
}

/**
 * Fetch the on-screen guidance pop-over for a human-only step (2026-08-20).
 * The computer-use plane composes the text/position/affordance from the live
 * page; a deterministic per-kind fallback ships when the planner is down, so
 * guidance ALWAYS appears. Null only on transport failure / old VM.
 */
export async function fetchOverlayGuide(
  sessionId: string,
  base: string,
  appName: string,
  kind: 'login' | '2fa' | 'challenge'
): Promise<OverlayGuide | null> {
  try {
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/overlay-guide`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appName, kind }),
      }
    );
    if (!res.ok) return null;
    const data = (await res.json().catch(() => null)) as { ok?: boolean; guide?: OverlayGuide } | null;
    if (!data || data.ok !== true || !data.guide || typeof data.guide.text !== 'string') return null;
    return data.guide;
  } catch {
    return null;
  }
}

/**
 * A navigate a running task refused (a 409 `another-task` from browser-service page-change.ts) is
 * tried again this many times, this far apart (2026-09-29, PR-5). The sign-in window takes its hold
 * the moment it has its desktop (usePersonHold), and the task waits for it from then on; the first
 * navigate can reach the workstation just before that hold does.
 */
export const NAVIGATE_BUSY_ATTEMPTS = 3;
export const NAVIGATE_BUSY_RETRY_MS = 1200;

/**
 * Deterministically point the session's visible browser at a URL via the
 * ANCIENT /action navigate verb — present on every browser-service build,
 * old or new. This is the retarget belt (2026-08-20 Kalshi repro): a reused
 * warm session on an old VM build kept showing its previous page, and the
 * pilot on that VM was unavailable — nobody took the user to the sign-in
 * page. Never throws; false = navigation did not happen.
 *
 * (2026-09-29, PR-5) It says it is the person's own sign-in window, and names
 * its app: the workstation lets a person's surface act on their own browser
 * unless a computer task is driving it and not waiting for them, and opens the
 * address in the app's own tab (or a new one), never over another app's tab
 * (page-change.ts). An older workstation ignores both.
 */
export async function navigateDesktopSession(
  sessionId: string,
  base: string,
  url: string,
  opts: { app?: string; wait?: (ms: number) => Promise<void>; stillWanted?: () => boolean } = {}
): Promise<boolean> {
  try {
    if (!/^https:\/\//i.test(url) || isSearchEngineSignInUrl(url)) return false;
    const app = typeof opts.app === 'string' ? opts.app.trim().slice(0, 80) : '';
    const wait = opts.wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    for (let attempt = 1; ; attempt += 1) {
      const res = await fetchWithBrowserServiceAuth(
        base,
        `${base}/api/desktop/${encodeURIComponent(sessionId)}/action`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Prest0n-Surface': 'sign-in-window' },
          body: JSON.stringify({ verb: 'navigate', url, ...(app ? { app } : {}) }),
        }
      );
      if (res.status === 409) {
        // A task is using the browser and is not yet waiting for this window: its hold is on the
        // way. Anything else refused (or the last attempt) is a navigation that did not happen.
        const busy = (await res.json().catch(() => null)) as { busy?: unknown; reason?: unknown } | null;
        if (busy?.busy === true && busy.reason === 'another-task' && attempt < NAVIGATE_BUSY_ATTEMPTS) {
          await wait(NAVIGATE_BUSY_RETRY_MS);
          if (opts.stillWanted && !opts.stillWanted()) return false; // the window closed meanwhile
          continue;
        }
        return false;
      }
      if (!res.ok) return false;
      // The /action route answers HTTP 200 with {ok:false} when Playwright's
      // goto failed (timeout, NXDOMAIN) — the body's ok is the truth (review
      // finding 2026-08-21: HTTP status alone reported phantom navigations).
      const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
      return data?.ok === true;
    }
  } catch {
    return false;
  }
}

/** What the paste bridge did — the surface renders one line per outcome. */
export type ClipboardPushResult = 'ok' | 'ok-pressed' | 'empty' | 'denied' | 'failed';

/**
 * Carry the USER'S OWN clipboard into Prest0n's browser (2026-09-03 owner
 * directive).
 *
 * The live view is a browser on another machine. Its clipboard is that
 * machine's clipboard — empty — so ⌘V inside the window pastes nothing, and
 * a one-time code sitting in the user's mail app has to be retyped by hand
 * against a timer. This reads the local clipboard (only ever inside the
 * user's own click, which is what makes the permission grantable) and hands
 * it to the VM, where their own ⌘V then works.
 *
 * The text is never logged, never stored, and never shown — not even in an
 * error. It goes one way: this browser → that browser's clipboard.
 *
 * Never throws; every failure is a verdict the surface can explain:
 *   'empty'  nothing is copied yet — say so instead of failing silently.
 *   'denied' the browser refused to share the clipboard (no permission, an
 *            insecure context, a browser without the API).
 *   'failed' the window could not be reached.
 */
export async function pushLocalClipboardToDesktop(
  sessionId: string,
  base: string,
  /**
   * Ask the workstation to PRESS paste as well as fill the clipboard (Miles,
   * 2026-09-18). Opt-in per call so an older workstation — which ignores the
   * flag and simply fills — degrades to the two-step it always was; the
   * 'ok-pressed' result is what distinguishes the two, so the surface never
   * claims a press that did not happen.
   */
  press = false,
): Promise<ClipboardPushResult> {
  const read = await readLocalClipboardText();
  if (read === 'denied' || read === 'empty') return read;
  let text = read.text;
  try {
    return await pushTextToDesktop(sessionId, base, text, press);
  } finally {
    // Do not keep the user's credential in a closure any longer than the
    // request needs it.
    text = '';
  }
}

/**
 * The person's own clipboard, read inside the click that asked for it (a
 * clipboard read outside a user gesture is refused by every browser). The
 * whole-screen view hands it to the workstation's clipboard route above; the
 * window view (PR-9b) puts it straight into the page, so nothing goes through
 * the workstation's clipboard at all. 'denied' when the browser would not share
 * it, 'empty' when nothing is copied. The text is never logged or kept.
 */
export async function readLocalClipboardText(): Promise<{ text: string } | 'denied' | 'empty'> {
  let text = '';
  try {
    const clip = (navigator as unknown as { clipboard?: { readText?: () => Promise<string> } }).clipboard;
    if (!clip?.readText) return 'denied';
    text = String((await clip.readText()) || '');
  } catch {
    return 'denied';
  }
  if (!text.trim()) return 'empty';
  return { text };
}

/**
 * Hand text the person already pasted to the window's clipboard route — the
 * POST half of pushLocalClipboardToDesktop, shared with the live view's own
 * paste (2026-09-23, viewer-keys.ts): a paste the viewer cannot carry itself
 * (curly quotes, emoji, long text) comes here from the viewer, because only
 * this page holds the workstation credential. The text is never logged,
 * stored, or shown. Never throws.
 */
export async function pushTextToDesktop(
  sessionId: string,
  base: string,
  text: string,
  press: boolean,
): Promise<'ok' | 'ok-pressed' | 'failed'> {
  try {
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/clipboard`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, ...(press ? { press: true } : {}) }),
      },
    );
    if (!res.ok) return 'failed';
    const data = (await res.json().catch(() => null)) as
      { ok?: boolean; pressed?: boolean } | null;
    if (data?.ok !== true) return 'failed';
    // `pressed` is the workstation's own report. An older build has no such
    // field, and a newer one says false when the press could not be dispatched
    // — both mean the clipboard is loaded and the person presses it, which is
    // the honest thing to tell them.
    return data.pressed === true ? 'ok-pressed' : 'ok';
  } catch {
    return 'failed';
  }
}

/**
 * Is the page ON COURSE for this app — the client mirror of the workstation's
 * arrival belt (signin-classify.ts `hostRelevantToApp(url, …) || isAuthHost(url)`,
 * contract C5, 2026-09-02): the app's own host (its name token in the
 * hostname, or the researched destination's registrable domain, ccTLD-aware)
 * OR an auth surface — the accounts./login./sso. prefix family or a tenant
 * IdP such as acme.okta.com. An enterprise-SSO or MFA page is where the
 * sign-in IS happening, so the modal's off-app watch must never re-drive
 * away from it (P0 #9). The host rules are imported from signinHosts.ts —
 * the four-way parity test pins that no inline copy drifts. Pure; a
 * non-URL is never on course.
 */
export function pageBelongsToApp(
  url: string,
  appName: string,
  expectedUrl?: string
): boolean {
  const token = (appName || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  let host = '';
  try { host = new URL(url).hostname.toLowerCase(); } catch { return false; }
  if (token && token.length >= 3 && host.replace(/[^a-z0-9]+/g, '').includes(token)) return true;
  if (isAuthHost(host)) return true;
  try {
    if (expectedUrl) {
      const expected = new URL(expectedUrl).hostname.toLowerCase();
      // Registrable-domain tail, ccTLD-aware (signinHosts.registrableDomain):
      // under co.uk/com.au-style multi-label suffixes a 2-label tail is just
      // the suffix itself, which made ANY two .co.uk hosts "the same app"
      // (review finding 2026-08-21).
      if (host === expected || registrableDomain(host) === registrableDomain(expected)) return true;
    }
  } catch { /* bad expected url */ }
  return false;
}

/** Outcome of prest0n driving the session's browser to the sign-in page. */
export type SignInPilotOutcome = 'login-form' | 'stuck' | 'wrong-destination' | 'unavailable';

/** WHERE the drive ended up, as the workstation classified it (contract C4,
 *  2026-09-02). Absent on a VM build that predates the landing fields — the
 *  surface then reports nothing and keeps today's copy. `kind` falls back to
 *  'other' (unclassified) rather than inventing a classification. */
export interface SignInPilotLanding {
  url: string;
  kind: DesktopPageKind;
  status?: number;
  title?: string;
}

/**
 * Ask the session's VM to DRIVE its visible browser to appName's sign-in
 * page (computer-use tier-19 loop; navigate/click/scroll only — the pilot
 * cannot type by construction). Old VMs without the route (404 catch-all)
 * and any transport failure resolve to 'unavailable': the sign-in surface
 * then behaves exactly as before the pilot existed. Never throws.
 */
export async function runSignInPilotTask(
  sessionId: string,
  appName: string,
  base?: string,
  signInUrl?: string
): Promise<{ outcome: SignInPilotOutcome; detail?: string; landing?: SignInPilotLanding }> {
  try {
    const resolvedBase = (base && base.trim()) || (await getBrowserServiceBaseUrl());
    logConnectFunnel({ event: 'pilot_started', detail: appName });
    const res = await fetchWithBrowserServiceAuth(
      resolvedBase,
      `${resolvedBase}/api/desktop/${encodeURIComponent(sessionId)}/signin-pilot`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // signInUrl (2026-08-20): the pilot navigates straight to the
        // researched official page and reconciles its verdict against the
        // live URL — never a search-engine hop (the Bing-captcha repro).
        body: JSON.stringify({
          appName,
          ...(signInUrl && /^https:\/\//i.test(signInUrl) ? { signInUrl } : {}),
        }),
      }
    );
    if (res.status === 404) {
      logConnectFunnel({ event: 'pilot_unavailable', detail: 'route missing (old VM build)' });
      return { outcome: 'unavailable', detail: 'route missing' };
    }
    if (!res.ok) {
      logConnectFunnel({ event: 'pilot_unavailable', detail: `HTTP ${res.status}` });
      return { outcome: 'unavailable', detail: `HTTP ${res.status}` };
    }
    const data = (await res.json().catch(() => null)) as
      | {
          loginFormReached?: boolean; stuck?: string; degrade?: string;
          landedUrl?: string; landedOffApp?: boolean;
          landedKind?: string; landedStatus?: number; landedTitle?: string;
        }
      | null;
    if (!data || data.degrade) {
      logConnectFunnel({ event: 'pilot_unavailable', detail: String(data?.degrade || 'bad response') });
      return { outcome: 'unavailable', detail: String(data?.degrade || 'bad response') };
    }
    // The landing, when the workstation reported one (contract C4). Built
    // from whichever fields arrived — an old VM sends landedUrl alone, a
    // current one adds the classification the self-heal report needs.
    const landedUrl = typeof data.landedUrl === 'string' ? data.landedUrl : '';
    const landedKindRaw = typeof data.landedKind === 'string' ? data.landedKind : '';
    const landedStatus =
      typeof data.landedStatus === 'number' && Number.isFinite(data.landedStatus)
        ? Math.trunc(data.landedStatus)
        : undefined;
    const landedTitle = typeof data.landedTitle === 'string' ? data.landedTitle : '';
    const landing: SignInPilotLanding | undefined =
      landedUrl || landedKindRaw || landedStatus !== undefined || landedTitle
        ? {
            url: landedUrl,
            kind: DESKTOP_PAGE_KINDS.includes(landedKindRaw as DesktopPageKind)
              ? (landedKindRaw as DesktopPageKind)
              : 'other',
            ...(landedStatus !== undefined ? { status: landedStatus } : {}),
            ...(landedTitle ? { title: landedTitle.slice(0, 200) } : {}),
          }
        : undefined;
    if (data.loginFormReached) {
      logConnectFunnel({ event: 'pilot_arrived', detail: appName });
      return { outcome: 'login-form', ...(landing ? { landing } : {}) };
    }
    if (data.landedOffApp) {
      // The destination itself redirected off the app (the Fitbit store
      // class) — a distinct, actionable verdict, with the landing logged.
      logConnectFunnel({ event: 'pilot_wrong_destination', detail: String(data.landedUrl || '').slice(0, 200) });
      return { outcome: 'wrong-destination', detail: data.landedUrl, ...(landing ? { landing } : {}) };
    }
    logConnectFunnel({ event: 'pilot_stuck', detail: String(data.stuck || '') });
    return { outcome: 'stuck', detail: data.stuck, ...(landing ? { landing } : {}) };
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    logConnectFunnel({ event: 'pilot_unavailable', detail });
    return { outcome: 'unavailable', detail };
  }
}

/**
 * Promote a custom connection to 'connected' (user-attested). Uses the
 * composioApi custom-connections route; server-gated on integrations.manage
 * — the same gate registration already requires.
 */
export async function completeCustomConnection(
  slug: string,
  signedInAs?: string,
  signInUrl?: string,
  /** W-R3 (2026-08-25): finish onto the caller's own plane — the scope
   *  workspace's registration husk is cleaned up server-side and the doc
   *  records where the login physically lives (profileAccountId). */
  personalOnly?: boolean,
  /** Slice F3: the desktop the sign-in ran on. The server reads that desktop's own evidence over the
   *  machine channel; this names WHICH desktop only, never what it saw. */
  desktopSessionId?: string,
): Promise<{ resumedJobs: number; usernameWanted?: boolean }> {
  const API_BASE = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  const res = await fetch(`${API_BASE}/composioApi/composio/custom-connections`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      action: 'markConnected',
      slug,
      ...(signedInAs && signedInAs.trim() ? { signedInAs: signedInAs.trim().slice(0, 120) } : {}),
      // Persist the truth-verifier's probe target at promotion time.
      ...(signInUrl && /^https:\/\//i.test(signInUrl) ? { signInUrl: signInUrl.slice(0, 500) } : {}),
      ...(personalOnly ? { personalOnly: true } : {}),
      ...(desktopSessionId && /^desktop-[A-Za-z0-9_-]{1,120}$/.test(desktopSessionId) ? { desktopSessionId } : {}),
      // Resume on sign-in (2026-09-26): the server resumes any job that was waiting for this
      // sign-in, and this page stands down its own resume line for it (connectSignedInEvent).
      resumeJobs: true,
    }),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(String((errBody as any)?.message || (errBody as any)?.error || `HTTP ${res.status}`));
  }
  logConnectFunnel({ event: 'custom_connected', toolkitSlug: slug });
  // An older server answers without the count: nothing resumed, so the resume line still goes.
  const body = await res.json().catch(() => ({}));
  const resumedJobs = Number((body as { resumedJobs?: unknown })?.resumedJobs);
  // S3: whether the window offers the username field, from the save's own record. Only a boolean counts; an
  // older server answers without it and the window decides from its last poll.
  const usernameWanted = (body as { usernameWanted?: unknown })?.usernameWanted;
  return {
    resumedJobs: Number.isFinite(resumedJobs) && resumedJobs > 0 ? Math.floor(resumedJobs) : 0,
    ...(typeof usernameWanted === 'boolean' ? { usernameWanted } : {}),
  };
}

/**
 * Fire the browser-lane truth verifier (2026-08-20): asks the server to
 * re-probe every 'connected' custom connection against the account VM and
 * downgrade any whose saved browser is signed out to 'signin-lost' (→ the
 * integrations "finish sign-in" tile). Fire-and-forget from the integrations
 * page on load; server-throttled 6h and never wakes a sleeping VM. Never
 * throws — connection truth must never be the thing that breaks a page load.
 */
export async function verifyCustomSignins(): Promise<void> {
  try {
    const API_BASE = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';
    const user = getAuth().currentUser;
    if (!user) return;
    const token = await user.getIdToken();
    await fetch(`${API_BASE}/composioApi/composio/custom-connections`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action: 'verifyCustomSignins' }),
    });
  } catch {
    /* best effort — page load never depends on this */
  }
}

// ─── Co-Drive journey (Phase 0 — docs/FRAMEWORK-computer-use-co-drive.md §6) ───
//
// The journey doc formalizes this flow's implicit state on the VM side: WHO
// holds the wheel (control), WHAT is blocking (typed wall), and what happened
// (receipts), persisted under the runtime org account. The client's ONLY
// journey-driven chrome in Phase 0 is the persistent pause control — every
// other render keeps its local derivation, so old VM builds (404 on these
// routes) and the platform-shared host (accountId mismatch degrade) behave
// exactly as before. Both helpers never throw.

export type JourneyControlAction = 'pause' | 'resume' | 'attest_done' | 'close';

export interface SignInJourneyOpenResult {
  journeyId: string;
  accountId: string;
}

/**
 * Open (or adopt — idempotent per session) the sign-in journey on the VM.
 * Null on ANY failure: old build (404), transport, or the machine-readable
 * degrade (accountId mismatch = shared host / mis-resolved org). Null means
 * "no journey chrome" — never an error state.
 */
export async function openSignInJourney(
  sessionId: string,
  base: string,
  opts: {
    appName: string;
    accountId: string;
    slug?: string | null;
    signInUrl?: string | null;
    source?: string | null;
    userId?: string | null;
  }
): Promise<SignInJourneyOpenResult | null> {
  try {
    if (!opts.appName || !opts.accountId) return null;
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/journey`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'signin',
          appName: opts.appName,
          accountId: opts.accountId,
          ...(opts.slug ? { slug: opts.slug } : {}),
          ...(opts.signInUrl && /^https:\/\//i.test(opts.signInUrl) ? { signInUrl: opts.signInUrl } : {}),
          ...(opts.source ? { source: opts.source } : {}),
          ...(opts.userId ? { userId: opts.userId } : {}),
        }),
      }
    );
    if (!res.ok) return null; // 404 = old VM build — stand down silently
    const data = (await res.json().catch(() => null)) as
      | { ok?: boolean; journeyId?: string; accountId?: string; degrade?: string }
      | null;
    if (!data || data.ok !== true || typeof data.journeyId !== 'string' || typeof data.accountId !== 'string') {
      return null;
    }
    return { journeyId: data.journeyId, accountId: data.accountId };
  } catch {
    return null;
  }
}

/**
 * Drive the journey's control lane: pause (human freezes the AI), resume
 * (routes through verifying on the VM), attest_done (fresh-observation
 * verdict). True only when the VM confirmed. Never throws.
 */
export async function postJourneyControl(
  sessionId: string,
  base: string,
  action: JourneyControlAction
): Promise<boolean> {
  try {
    const res = await fetchWithBrowserServiceAuth(
      base,
      `${base}/api/desktop/${encodeURIComponent(sessionId)}/journey/control`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      }
    );
    if (!res.ok) return false;
    const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
    return data?.ok === true;
  } catch {
    return false;
  }
}
